import { createSlice, PayloadAction, createSelector, createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { updateCaseNote } from '../../../thunks/CaseNote';
import { 
    fetchCaseOverview as fetchCaseOverviewThunk,
    fetchAdditionalAnalysis,
    fetchBorrowerVerifications,
    fetchInterviewsStep,
    fetchPortfolioAnalysis
} from "../../../thunks/AnalysisStep";
import { CaseNote, CaseNoteType } from '../../../api/CaseNote';
import { RootState } from '../../../app/store';


export interface CaseNoteEntry {
    status: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
    notes: EntityState<CaseNote, string>;
}

export interface CaseNotesDataState  {
    [caseId: string]: CaseNoteEntry;
}

const caseNotesAdapter = createEntityAdapter<CaseNote, string>({
    selectId: (note) => note.type
})

const initialState: CaseNotesDataState = {};

const caseNotesSlice = createSlice({
    name: 'caseNotesData',
    initialState,
    reducers: {
        resetCaseNotes: () => ({}),
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCaseOverviewThunk.pending, (state, action) => {
                const caseId = action.meta.arg.case_id;
                if (!caseId) {return};
                
                if (!state[caseId]) {
                    state[caseId] = {
                        status: 'idle',
                        error: null,
                        notes: caseNotesAdapter.getInitialState(),
                    };
                }
                state[caseId].status = 'pending';
                state[caseId].error = null;
            })
            .addCase(fetchCaseOverviewThunk.fulfilled, (state, action) => {
                const caseId = action.meta.arg.case_id;
                if (!caseId || !state[caseId]) {return;}

                state[caseId].status = 'succeeded';
                state[caseId].error = null;
                const remarks = action.payload.remarks
                if (remarks) {
                    caseNotesAdapter.upsertOne(state[caseId].notes, remarks)
                }
            })
            .addCase(fetchCaseOverviewThunk.rejected, (state, action) => {
                const caseId = action.meta.arg.case_id;

                if (!caseId || !state[caseId]) {
                    return;
                }
                
                state[caseId].status = 'failed';
                state[caseId].error = action.error.message || 'An unknown error occurred while fetching remarks.';
            })
            .addCase(updateCaseNote.pending, (state, action) => {
                const caseId = action.meta.arg.caseId;

                if (caseId && state[caseId]) {
                    state[caseId].status = 'pending';
                    state[caseId].error = null;
                }
            })
            .addCase(updateCaseNote.fulfilled, (state, action) => {
                const caseId = action.meta.arg.caseId;
                const noteType = action.meta.arg.type as CaseNoteType;
                const request = action.meta.arg.request;
                
                state[caseId].status = 'succeeded';

                const updatedNote: CaseNote = {
                    type: noteType,
                    value: request.value,
                    user_updated: request.user_updated,
                    ai_generated: request.ai_generated || true,
                };
                
                caseNotesAdapter.upsertOne(state[caseId].notes, updatedNote);
            })
            .addCase(updateCaseNote.rejected, (state, action) => {
                const caseId = action.meta.arg.caseId;

                if (caseId && state[caseId]) {
                    state[caseId].status = 'failed';
                    state[caseId].error = action.error.message || 'Failed to save the note.';
                }
            })
            .addCase(fetchBorrowerVerifications.pending, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id) return;
                if (!state[case_id]) {
                    state[case_id] = {
                        status: 'idle',
                        error: null,
                        notes: caseNotesAdapter.getInitialState(),
                    };
                }
                state[case_id].status = 'pending';
                state[case_id].error = null;
            })
            .addCase(fetchBorrowerVerifications.fulfilled, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;
                state[case_id].status = 'succeeded';
                const { remarks } = action.payload;

                if (remarks) {
                    caseNotesAdapter.upsertOne(state[case_id].notes, remarks);
                }
            })
            .addCase(fetchBorrowerVerifications.rejected, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'failed';
                state[case_id].error = action.error.message || 'Failed to fetch borrower verification remarks.';
            })
            .addCase(fetchInterviewsStep.pending, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id) return;
                if (!state[case_id]) {
                    state[case_id] = {
                        status: 'idle',
                        error: null,
                        notes: caseNotesAdapter.getInitialState(),
                    };
                }
                state[case_id].status = 'pending';
                state[case_id].error = null;
            })
            .addCase(fetchInterviewsStep.fulfilled, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'succeeded';
                const { interview_findings } = action.payload;
                if (interview_findings) {
                    caseNotesAdapter.upsertOne(state[case_id].notes, interview_findings);
                }
            })
            .addCase(fetchInterviewsStep.rejected, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'failed';
                state[case_id].error = action.error.message || 'Failed to fetch interviews remarks.';
            })
            .addCase(fetchPortfolioAnalysis.pending, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id) return;
                if (!state[case_id]) {
                    state[case_id] = {
                        status: 'idle',
                        error: null,
                        notes: caseNotesAdapter.getInitialState(),
                    };
                }
                state[case_id].status = 'pending';
                state[case_id].error = null;
            })
            .addCase(fetchPortfolioAnalysis.fulfilled, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'succeeded';
                const { remarks } = action.payload;
                if (remarks) {
                    caseNotesAdapter.upsertOne(state[case_id].notes, remarks);
                }
            })
            .addCase(fetchPortfolioAnalysis.rejected, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'failed';
                state[case_id].error = action.error.message || 'Failed to fetch portfolio analysis remarks.';
            })
            .addCase(fetchAdditionalAnalysis.pending, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id) return;
                if (!state[case_id]) {
                    state[case_id] = {
                        status: 'idle',
                        error: null,
                        notes: caseNotesAdapter.getInitialState(),
                    };
                }
                state[case_id].status = 'pending';
                state[case_id].error = null;
            })
            .addCase(fetchAdditionalAnalysis.fulfilled, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'succeeded';
                const { remarks } = action.payload;
                if (remarks) {
                    caseNotesAdapter.upsertOne(state[case_id].notes, remarks);
                }
            })
            .addCase(fetchAdditionalAnalysis.rejected, (state, action) => {
                const { case_id } = action.meta.arg;
                if (!case_id || !state[case_id]) return;

                state[case_id].status = 'failed';
                state[case_id].error = action.error.message || 'Failed to fetch additional analysis remarks.';
            })
    }
})


export const caseNotesDataReducer = caseNotesSlice.reducer;

const selectCaseEntry = (
    state: RootState,
    caseId: string | undefined
) => {
    return caseId ? state.caseNotesData[caseId] : undefined;
}


export const selectCaseNotesStatus = createSelector(
    [selectCaseEntry],
    (entry) => entry?.status ?? 'idle'
);

export const selectCaseNotesError = createSelector(
    [selectCaseEntry],
    (entry) => entry?.error ?? null
);


const selectNotesEntities = createSelector(
    [selectCaseEntry],
    (entry) => entry?.notes
);


export const selectNoteByType = createSelector(
    [
        selectNotesEntities,
        (state: RootState, caseId: string | undefined, noteType: CaseNoteType) => noteType
    ],
    (notes, noteType) => {
        if (!notes) return undefined;
        return caseNotesAdapter.getSelectors().selectById(notes, noteType);
    }
);

export const selectAllNotesForCase = createSelector(
    [selectNotesEntities],
    (notes) => {
        if (!notes) return [];
        return caseNotesAdapter.getSelectors().selectAll(notes);
    }
);

export const { resetCaseNotes } = caseNotesSlice.actions;